import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Phase1Progress, Phase2Progress } from '../lib/extraction';
import { runExtraction } from '../lib/extraction';
import type { GetAccessToken } from '../lib/gmailApi';
import { reportError } from '../lib/errorReporter';

interface ProgressScreenProps {
  senders: string[];
  maxParallel: number;
  dateAfter: string;
  dateBefore: string;
  getAccessToken: GetAccessToken;
  onComplete: () => void;
}

type Phase = 'phase1' | 'phase2' | 'complete' | 'error';

interface SenderStatus {
  email: string;
  status: string;
  messageCount?: number;
  error?: string;
}

function formatTime(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

function getTarFilename(): string {
  return `gmail-extraction-${new Date().toISOString().slice(0, 10)}.tar`;
}

export const ProgressScreen: React.FC<ProgressScreenProps> = ({
  senders,
  maxParallel,
  dateAfter,
  dateBefore,
  getAccessToken,
  onComplete,
}) => {
  const [phase, setPhase] = useState<Phase>('phase1');
  const [senderStatuses, setSenderStatuses] = useState<SenderStatus[]>(
    senders.map((s) => ({ email: s, status: 'pending' }))
  );
  const [progressSubtitle, setProgressSubtitle] = useState('Starting...');
  const [phase1Progress, setPhase1Progress] = useState({
    scannedCount: 0,
    estimatedTotal: undefined as number | undefined,
    percent: undefined as number | undefined,
  });
  const [phase2Percent, setPhase2Percent] = useState(0);
  const [phase2Count, setPhase2Count] = useState({ completed: 0, failed: 0, total: 0 });
  const [phase2Times, setPhase2Times] = useState({ elapsed: 0, eta: 0 });
  const [completedPerSender, setCompletedPerSender] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const downloadStartedRef = useRef(false);

  const handleProgress = useCallback(
    (data: Phase1Progress | Phase2Progress) => {
      if (data.phase === 1) {
        setSenderStatuses((prev) => {
          const next = [...prev];
          while (next.length <= data.senderIndex) {
            next.push({ email: '', status: 'pending' });
          }
          next[data.senderIndex] = {
            email: data.sender,
            status: data.status === 'searching' ? 'searching...' : data.status,
            messageCount: data.messageCount,
            error: data.error,
          };
          return next;
        });
        if (data.scannedCount != null || data.estimatedTotal != null || data.percent != null) {
          setPhase1Progress({
            scannedCount: data.scannedCount ?? 0,
            estimatedTotal: data.estimatedTotal,
            percent: data.percent,
          });
        }
        setProgressSubtitle(
          data.sender === 'All inbox'
            ? 'Searching all inbox...'
            : `Searching sender ${data.senderIndex + 1} of ${data.senderCount}...`
        );
      } else if (data.phase === 2) {
        setPhase('phase2');
        setPhase2Percent(data.percent);
        setPhase2Count({
          completed: data.completed,
          failed: data.failed,
          total: data.total,
        });
        setPhase2Times({ elapsed: data.elapsed, eta: data.eta });
        setCompletedPerSender(data.completedPerSender);
        setProgressSubtitle('Downloading threads...');
      }
    },
    []
  );

  // File handle obtained from showSaveFilePicker before extraction starts
  const fileHandleRef = useRef<FileSystemFileHandle | null>(null);

  const handleTarStreamReady = useCallback((stream: ReadableStream<Uint8Array>) => {
    if (downloadStartedRef.current) return;
    downloadStartedRef.current = true;

    const onStreamError = (e: unknown) => {
      const msg = e instanceof Error ? e.message : 'Download failed';
      console.error('TAR stream failed:', e);
      reportError('TAR stream failed', { phase: 'tar-stream' }, e);
      setPhase('error');
      setError(`TAR download failed: ${msg}`);
    };

    const fileHandle = fileHandleRef.current;
    if (fileHandle) {
      // File System Access API — stream directly to user-chosen file
      fileHandle
        .createWritable()
        .then((writable) => stream.pipeTo(writable))
        .catch(onStreamError);
    } else {
      // Blob fallback (Firefox/Safari) — collect chunks, download at end
      const reader = stream.getReader();
      const chunks: Uint8Array[] = [];
      (async () => {
        try {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            if (value) chunks.push(value);
          }
          const blob = new Blob(chunks as unknown as BlobPart[], { type: 'application/x-tar' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = getTarFilename();
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }, 100);
        } catch (e) {
          onStreamError(e);
        }
      })();
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        // Try File System Access API first (Chrome/Edge — works on Windows)
        // Falls back to Blob download for Firefox/Safari
        if ('showSaveFilePicker' in window) {
          try {
            fileHandleRef.current = await (window as unknown as { showSaveFilePicker: (opts: unknown) => Promise<FileSystemFileHandle> }).showSaveFilePicker({
              suggestedName: getTarFilename(),
              types: [
                {
                  description: 'TAR archive',
                  accept: { 'application/x-tar': ['.tar'] },
                },
              ],
            });
          } catch (e) {
            // User cancelled the save dialog
            if (e instanceof DOMException && e.name === 'AbortError') {
              setPhase('error');
              setError('Save dialog cancelled. Please start a new extraction.');
              return;
            }
            // showSaveFilePicker failed for another reason — fall back to Blob
            fileHandleRef.current = null;
          }
        }

        const result = await runExtraction(
          senders,
          maxParallel,
          dateAfter,
          dateBefore,
          getAccessToken,
          handleProgress,
          handleTarStreamReady
        );

        if (cancelled) return;

        if (result.total === 0) {
          setProgressSubtitle('Complete!');
          setPhase('complete');
          setError(
            senders.length > 0
              ? 'No threads found for the specified senders.'
              : 'No threads found in inbox or sent.'
          );
        } else if (result.failed > 0 && result.completed === 0) {
          setPhase('error');
          setError(`All ${result.failed} thread(s) failed to extract.`);
        } else {
          setProgressSubtitle('Complete!');
          setPhase('complete');
          if (result.failed > 0) {
            setError(
              `${result.completed} thread(s) extracted, ${result.failed} failed.`
            );
          }
        }
      } catch (e) {
        if (!cancelled) {
          reportError('Extraction failed', { phase: 'runExtraction' }, e);
          setPhase('error');
          setError(e instanceof Error ? e.message : 'Extraction failed');
        }
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [senders, maxParallel, dateAfter, dateBefore, getAccessToken, handleProgress, handleTarStreamReady]);

  return (
    <div className="ge-progress">
      <header className="ge-hero">
        <h1 className="ge-headline">Extracting Emails</h1>
        <p className="ge-subheadline">{progressSubtitle}</p>
      </header>

      {phase === 'phase1' && (
        <div className="ge-phase1 ge-phase-card">
          <h3 className="ge-phase-label">Phase 1 — Collecting thread IDs</h3>
          {(phase1Progress.scannedCount > 0 || phase1Progress.percent != null) && (
            <div className="ge-progress-bar-container ge-phase1-progress">
              <div className="ge-progress-bar">
                <div
                  className={
                    phase1Progress.percent != null
                      ? 'ge-progress-bar-fill'
                      : 'ge-progress-bar-fill ge-progress-bar-indeterminate'
                  }
                  style={
                    phase1Progress.percent != null
                      ? { width: `${phase1Progress.percent}%` }
                      : undefined
                  }
                />
              </div>
              <div className="ge-progress-stats">
                {phase1Progress.percent != null ? (
                  <span>{phase1Progress.percent}%</span>
                ) : null}
                <span>
                  {phase1Progress.scannedCount.toLocaleString()} threads scanned
                  {phase1Progress.estimatedTotal != null
                    ? ` (est. ${phase1Progress.estimatedTotal.toLocaleString()})`
                    : ''}
                </span>
              </div>
            </div>
          )}
          <div className="ge-sender-list">
            {senderStatuses.map((s) => (
              <div key={s.email} className="ge-sender-row">
                <span className="ge-sender-email">{s.email}</span>
                <span
                  className={`ge-sender-status ${s.status === 'searching...' ? 'searching' : ''} ${s.status === 'done' ? 'done' : ''}`}
                  style={s.error ? { color: 'var(--ge-error)' } : undefined}
                >
                  {s.status === 'done' && s.messageCount !== undefined
                    ? `${s.messageCount} thread(s)`
                    : s.error
                      ? `error: ${s.error}`
                      : s.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(phase === 'phase2' || phase === 'complete') && (
        <div className="ge-phase2 ge-phase-card">
          <h3 className="ge-phase-label">Phase 2 — Downloading threads</h3>
          <p className="ge-stream-info">
            TAR is being saved to disk. If extraction fails, the partial file remains extractable.
          </p>
          <div className="ge-progress-bar-container">
            <div className="ge-progress-bar">
              <div
                className="ge-progress-bar-fill"
                style={{ width: `${phase2Percent}%` }}
              />
            </div>
            <div className="ge-progress-stats">
              <span>{phase2Percent}%</span>
              <span>
                {phase2Count.completed + phase2Count.failed} / {phase2Count.total}
              </span>
            </div>
            <div className="ge-progress-times">
              <span>
                Elapsed: <strong>{formatTime(phase2Times.elapsed)}</strong>
              </span>
              <span>
                ETA: <strong>{formatTime(phase2Times.eta * 1000)}</strong>
              </span>
              {phase2Count.failed > 0 && (
                <span>
                  Failed: <strong>{phase2Count.failed}</strong>
                </span>
              )}
            </div>
          </div>
          <div className="ge-phase-card ge-sender-downloads">
            <h3 className="ge-phase-label">Extracted senders</h3>
            <div className="ge-sender-list">
              {senderStatuses.map((s) => {
                const total = s.messageCount ?? 0;
                const done = completedPerSender[s.email] ?? 0;
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                return (
                  <div key={s.email} className="ge-sender-row ge-sender-row-progress">
                    <span className="ge-sender-email">{s.email}</span>
                    <span className="ge-sender-meta">
                      {total > 0 ? `${done} / ${total} thread(s)` : '0 thread(s)'}
                    </span>
                    <div className="ge-sender-progress-wrap">
                      <div className="ge-sender-progress">
                        <div
                          className="ge-sender-progress-fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {phase === 'complete' && (
        <div className="ge-completion ge-phase-card">
          <div className="ge-completion-icon">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle
                cx="32"
                cy="32"
                r="30"
                fill="#22c55e"
                opacity="0.15"
              />
              <path
                d="M20 32l8 8 16-16"
                stroke="#22c55e"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="ge-completion-text">
            {error ??
              `${phase2Count.completed} thread(s) extracted${phase2Count.failed > 0 ? ` (${phase2Count.failed} failed)` : ''}`}
          </p>
          <p className="ge-completion-hint">
            TAR file saved successfully.
          </p>
          <div className="ge-completion-buttons">
            <button
              type="button"
              className="ge-btn-secondary"
              onClick={onComplete}
            >
              New Extraction
            </button>
          </div>
        </div>
      )}

      {phase === 'error' && (
        <div className="ge-completion ge-phase-card">
          <p className="ge-completion-text ge-error">{error}</p>
          <p className="ge-completion-hint">
            If extraction started, a partial TAR may be in your Downloads folder.
          </p>
          <button
            type="button"
            className="ge-btn-secondary"
            onClick={onComplete}
          >
            New Extraction
          </button>
        </div>
      )}
    </div>
  );
};
